import React, { useState, MouseEvent, useEffect, useRef } from 'react';

import { Drawer, Typography } from '@mui/material';
import Divider from '@mui/material/Divider';

import Box from '@mui/system/Box';
import Avatar from '@mui/material/Avatar';

import SidebarLink from '@/components/shared/Sidebar/SidebarLink';
import BrandSelect from '@/components/shared/SelectLists/BrandSelect';
import HeaderPropertyLogo from '@/components/shared/SelectLists/PropertySelect';

import { basicLinkStyles } from './styles';

import avatar from '@/assets/avatar.png';
import SettingsIcon from '@/assets/icons/settings.svg?react'; // eslint-disable-line import/no-unresolved
import AppIcon from '@/assets/icons/app-logo.png'; // eslint-disable-line import/no-unresolved
import ProfileDropdown from '../ProfileDropdown';

import { useSidebarNavigation } from '@/contexts/sidebarNavigationContext';
import DynamicContentSections from '@/components/shared/Sidebar/DynamicContentSections';
import SecondarySidebar from '@/components/shared/Sidebar/SecondarySidebar';
import { useUserData } from '@/hooks/useUserData';
import { useLocation } from 'react-router-dom';

export type SidebarProps = {
  brand_id?: string;
  property_id?: string;
  dbsProgress?: boolean;
  propertyDropdownVisible?: boolean;
  headerHeight?: number;
  footerHeight?: number;
  compactSidebarMenus?: boolean;
};

const Sidebar: React.FC<SidebarProps> = (props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState<number>(135);
  const [footerHeight, setFooterHeight] = useState<number>(128);
  const [compactSidebarMenus, setCompactSidebarMenus] = useState<boolean>(false);
  const location = useLocation();
  const {
    sidebarStyle,
    showSettingsSidebar,
    secondarySidebarData,
    updateValue,
    activeSettingsLink,
  } = useSidebarNavigation();
  const { userData } = useUserData();
  const userType = Number(userData?.type || 0);
  const showPropertySelector = [3, 4].includes(userType);
  const showBrandSelector = [1, 2, 3, 4].includes(userType) || !userType;
  const isPartnerProfilePage = location.pathname.startsWith('/partnerProfile/');
  const hasActiveSettingsSection = Boolean(activeSettingsLink.activeSection);
  const shouldShowSecondarySidebar =
    !secondarySidebarData.isEmpty && !showSettingsSidebar && !hasActiveSettingsSection;

  /*
    Keep content/header offset in sync only with visible nav states.
   */
  useEffect(() => {
    updateValue('showSecondaryNav', shouldShowSecondarySidebar || hasActiveSettingsSection);
  }, [shouldShowSecondarySidebar, hasActiveSettingsSection, updateValue]);

  useEffect(() => {
    const syncCompactMode = () => {
      const availableMenuHeight = window.innerHeight - headerHeight - footerHeight;
      // Keep normal menu on laptops; collapse only when the real available area becomes tight.
      setCompactSidebarMenus(availableMenuHeight < 340);
    };

    syncCompactMode();
    window.addEventListener('resize', syncCompactMode);

    return () => {
      window.removeEventListener('resize', syncCompactMode);
    };
  }, [headerHeight, footerHeight, showPropertySelector, showBrandSelector]);

  useEffect(() => {
    const headerElement = headerRef.current;
    if (!headerElement) {
      return;
    }

    const syncHeaderHeight = () => {
      const measuredHeight = Math.ceil(headerElement.getBoundingClientRect().height);
      if (measuredHeight > 0) {
        setHeaderHeight(measuredHeight);
      }
    };

    syncHeaderHeight();

    const resizeObserver = new ResizeObserver(() => syncHeaderHeight());
    resizeObserver.observe(headerElement);
    window.addEventListener('resize', syncHeaderHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', syncHeaderHeight);
    };
  }, [sidebarStyle, showPropertySelector, showBrandSelector]);

  useEffect(() => {
    const footerElement = footerRef.current;
    if (!footerElement) {
      return;
    }

    const syncFooterHeight = () => {
      const measuredHeight = Math.ceil(footerElement.getBoundingClientRect().height);
      if (measuredHeight > 0) {
        setFooterHeight(measuredHeight);
      }
    };

    syncFooterHeight();

    const resizeObserver = new ResizeObserver(() => syncFooterHeight());
    resizeObserver.observe(footerElement);
    window.addEventListener('resize', syncFooterHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', syncFooterHeight);
    };
  }, [sidebarStyle, userData?.email, userData?.first_name]);

  const handleOnUserClick = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl((prev) => (prev ? null : e.currentTarget));
    showSettingsSidebar && updateValue('showSettingsSidebar', false);
  };

  const handleMouseEnter = () => updateValue('isHovered', true);

  const handleMouseLeave = () => updateValue('isHovered', false);

  return (
    <Drawer
      sx={{
        '& .MuiDrawer-paper': {
          overflow: 'visible',
          boxSizing: 'border-box',
          width: shouldShowSecondarySidebar ? '215px' : '65px',
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'row',
          backgroundColor: 'transparent',
          zIndex: 1200,
          border: 'none',
          transition: 'box-shadow .28s ease',
          pointerEvents: 'none',
        },
      }}
      variant='permanent'
      open={true}
    >
      {/* Primary nav. */}
      <Box className='primarynav' sx={{ pointerEvents: 'auto' }}>
        <Box
          className='primarynav-hover-area'
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{
            background:
              'linear-gradient(180deg, rgba(15, 23, 42, 0.98) 0%, rgba(15, 23, 42, 0.94) 100%)',
            borderRight: '1px solid rgba(148, 163, 184, 0.18)',
            boxShadow: '8px 0 24px -20px rgba(15, 23, 42, 0.85)',
            padding: '10px',
            boxSizing: 'border-box',
            position: 'absolute',
            height: '100%',
            width: sidebarStyle === 'full' ? '100%' : '65px',
            transition: 'width .28s ease-in',
            '.brand_logo_icon': {
              opacity: '0',
              transition: 'opacity .3s',
            },
            ...(sidebarStyle === 'mini'
              ? {
                  '.sidebar__user-info p, .sidebar__app-name, .brand-select__text, .brand-select__caret':
                    {
                      transition: 'color .3s, max-width .3s',
                      color: 'transparent',
                      maxWidth: '0',
                      overflow: 'hidden',
                    },
                  '.sidebar__linkset__title, a p, button p': {
                    fontSize: '0 !important',
                    transition: 'font-size .3s',
                  },
                  '.sidebar__linkset__title + div': {
                    color: 'transparent',
                    transition: 'color .3s',
                  },
                  '.brand-select__button': {
                    minHeight: '36px',
                    padding: '3px',
                  },
                  '.brand-select__logo': {
                    width: '28px',
                    height: '28px',
                    borderRadius: '8px',
                  },
                  '.brand-select-shell': {
                    marginTop: '8px',
                  },
                  ...(!showSettingsSidebar &&
                  !hasActiveSettingsSection &&
                  !shouldShowSecondarySidebar &&
                  !isPartnerProfilePage
                    ? {
                        '&:hover': {
                          width: '215px',
                          '.sidebar__header': { width: '215px' },
                          '.sidebar__footer': { width: '205px' },
                          '.sidebar__user-info p, .sidebar__app-name, .brand-select__text, .brand-select__caret':
                            {
                              color: 'typography.main',
                              maxWidth: '150px',
                            },
                          '.sidebar__linkset__title, a p, button p': {
                            fontSize: '12px !important',
                          },
                          '.sidebar__linkset__title + div': {
                            color: 'unset',
                          },
                          '.brand-select__button': {
                            minHeight: '46px',
                            padding: '6px',
                          },
                          '.brand-select__logo': {
                            width: '34px',
                            height: '34px',
                            borderRadius: '9px',
                          },
                        },
                      }
                    : {}),
                }
              : {}),
          }}
        >
          {/* Sidebar Header. */}
          {/* TODO: Think on encapsulating this into own component. */}
          <Box
            className='sidebar__header'
            ref={headerRef}
            sx={{
              transition: 'width .28s ease-in',
              boxSizing: 'border-box',
              height: 'auto',
              position: 'fixed',
              top: '0',
              left: '0',
              padding: '18px 10px 0',
              width: sidebarStyle === 'full' ? '215px' : '65px',
            }}
          >
            <Box display='flex' gap={2} alignItems='center'>
              <img src={AppIcon} alt='app logo' style={{ width: '38px', height: '38px' }} />
              <Box className='sidebar__app-name'>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, lineHeight: 1.25 }}>
                  Partnership
                  <br />
                  Net
                </Typography>
              </Box>
            </Box>
            {/* Property Search. */}
            {showPropertySelector && <HeaderPropertyLogo />}

            {/* Brand Search. */}
            {showBrandSelector && <BrandSelect />}
            <Divider sx={{ borderColor: 'rgba(148, 163, 184, 0.35)', mt: '10px' }} />
          </Box>

          {/* Sidebar Content. */}
          <DynamicContentSections
            propertyDropdownVisible={showPropertySelector}
            headerHeight={headerHeight}
            footerHeight={footerHeight}
            compactSidebarMenus={compactSidebarMenus}
            {...props}
          />

          {/* Sidebar Footer. */}
          {/* TODO: Think on encapsulating this into own component. */}
          <Box
            className='sidebar__footer'
            ref={footerRef}
            sx={{
              transition: 'width .28s ease-in',
              position: 'fixed',
              bottom: '0',
              width: sidebarStyle === 'full' ? '205px' : '65px',
              padding: '0 10px 8px',
              margin: '0 -10px',
              background:
                'linear-gradient(180deg, rgba(15, 23, 42, 0) 0%, rgba(2, 6, 23, 0.65) 30%, rgba(2, 6, 23, 0.95) 100%)',
              zIndex: 2,
            }}
          >
            <Divider sx={{ borderColor: 'rgba(148, 163, 184, 0.35)' }} />
            <SidebarLink
              icon={SettingsIcon}
              text='Settings'
              boxContainerStyles={basicLinkStyles}
              handleClick={() => updateValue('showSettingsSidebar', !showSettingsSidebar)}
            />
            <Box>
              <Divider sx={{ borderColor: 'rgba(148, 163, 184, 0.35)', mt: '10px' }} />
              <Box
                className='sidebar__user-info'
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: '10px',
                  '& .MuiTypography-root': {
                    color: 'typography.main',
                    fontSize: '12px',
                    fontWeight: 500,
                  },
                }}
                onClick={handleOnUserClick}
              >
                <Avatar alt='profile' src={avatar} sx={{ position: 'absolute', left: '10px' }} />
                <Box sx={{ ml: '50px' }}>
                  <Typography
                    sx={{
                      textOverflow: 'ellipsis',
                      overflow: 'hidden !important',
                      maxWidth: '160px !important',
                      lineHeight: 1.3,
                    }}
                  >
                    {userData.first_name}
                  </Typography>
                  <Typography
                    sx={{
                      textOverflow: 'ellipsis',
                      overflow: 'hidden !important',
                      maxWidth: '160px !important',
                      lineHeight: 1.3,
                      opacity: 0.8,
                    }}
                  >
                    {userData.email}
                  </Typography>
                  <ProfileDropdown anchorElOutside={anchorEl} />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Secondary nav. */}
      {shouldShowSecondarySidebar && <SecondarySidebar />}
    </Drawer>
  );
};
export default Sidebar;
